import { useLocation, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle } from "lucide-react";

interface BookingConfirmationData {
  booking_number: string;
  tour_name: string;
  customer_name: string;
  email: string;
  booking_date: string;
  tour_schedule: string;
  adults: number;
  total_price: number;
  payment_method: string;
}

const TourBookingConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const bookingData = location.state as BookingConfirmationData;

  if (!bookingData) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">No booking data found</h1>
            <Button onClick={() => navigate("/tours")}>Back to Tours</Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 dark:from-gray-900 dark:to-gray-800">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto">
            {/* Success Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 dark:bg-green-900/20 rounded-full mb-4">
                <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400" />
              </div>
              <h1 className="text-3xl font-bold text-green-800 dark:text-green-200 mb-2">
                ¡Gracias por Reservar con Nosotros!
              </h1>
              <p className="text-lg text-green-700 dark:text-green-300">
                Tu reserva ha sido confirmada exitosamente
              </p>
            </div>

            {/* Booking Details Card */}
            <Card className="shadow-xl border-0 bg-white/90 backdrop-blur mb-6">
              <CardHeader className="bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-t-lg">
                <CardTitle className="text-center">
                  Detalles de tu Reserva
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Número de Reserva
                    </label>
                    <p className="text-lg font-bold text-primary">
                      {bookingData.booking_number}
                    </p>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Tour
                    </label>
                    <p className="font-semibold">{bookingData.tour_name}</p>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Cliente
                    </label>
                    <p className="font-semibold">{bookingData.customer_name}</p>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Email
                    </label>
                    <p className="font-semibold">{bookingData.email}</p>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Fecha del Tour
                    </label>
                    <p className="font-semibold">{bookingData.booking_date}</p>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Horario
                    </label>
                    <p className="font-semibold">{bookingData.tour_schedule}</p>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Viajeros
                    </label>
                    <p className="font-semibold">{bookingData.adults} Adulto{bookingData.adults > 1 ? 's' : ''}</p>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Método de Pago
                    </label>
                    <p className="font-semibold capitalize">{bookingData.payment_method}</p>
                  </div>
                </div>
                
                <div className="border-t pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold">Total Pagado</span>
                    <span className="text-2xl font-bold text-green-600">
                      ${bookingData.total_price}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Important Information */}
            <Card className="shadow-lg border-0 bg-blue-50 dark:bg-blue-900/20 mb-6">
              <CardHeader>
                <CardTitle className="text-blue-800 dark:text-blue-200">
                  Información Importante
                </CardTitle>
              </CardHeader>
              <CardContent className="text-blue-700 dark:text-blue-300 space-y-2">
                <p>• Recibirás un email de confirmación en los próximos minutos</p>
                <p>• Guarda tu número de reserva para futuras referencias</p>
                <p>• Cancelación gratuita hasta 24 horas antes del tour</p>
                <p>• Llega 15 minutos antes de la hora programada</p>
                <p>• Para cualquier consulta, contacta nuestro equipo de soporte</p>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate("/tours")}
                variant="outline"
                size="lg"
                className="w-full sm:w-auto"
              >
                Ver Más Tours
              </Button>
              <Button 
                onClick={() => navigate("/")}
                size="lg"
                className="w-full sm:w-auto"
              >
                Ir al Inicio
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TourBookingConfirmation;